import { useMemo, useState } from "react";
import { photoUrl } from "@/lib/api";
import { ChevronUp, ChevronDown, Star, Waves } from "lucide-react";
import { toast } from "sonner";

const CAPSIZE_POINTS = 15;
const SEASON_CAPSIZE_BUDGET = 5;

/**
 * A simple drag/reorder picker with up/down arrows (mobile-friendly).
 * value: array of yole ids (ordered by position, index 0 = pos 1)
 * onChange: new array
 * yoles: available yoles
 * minSize: min positions required (default 5)
 * budget: { max, used, remaining, per_stage } - season-wide capsize budget
 * currentStageId: id of the stage being edited (its picks don't double-count against the budget)
 */
export function RankingPicker({ value, onChange, yoles, minSize = 5, capsizePicks, onCapsizeChange, budget, currentStageId }) {
  const byId = useMemo(() => Object.fromEntries(yoles.map((y) => [y.id, y])), [yoles]);
  const chosen = value || [];
  const notChosen = yoles.filter((y) => !chosen.includes(y.id));

  const move = (from, to) => {
    if (to < 0 || to >= chosen.length) return;
    const next = [...chosen];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  const add = (yoleId) => {
    onChange([...chosen, yoleId]);
  };
  const remove = (yoleId) => {
    onChange(chosen.filter((id) => id !== yoleId));
  };

  // Season-wide capsize budget. When editing an existing pronostic for this stage,
  // its picks should NOT count as already-consumed against the budget (we're just re-picking them).
  const maxBudget = budget?.max ?? SEASON_CAPSIZE_BUDGET;
  const usedElsewhere = Math.max(
    0,
    (budget?.used ?? 0) - (currentStageId ? (budget?.per_stage?.[currentStageId] ?? 0) : 0),
  );
  const remainingSeason = Math.max(0, maxBudget - usedElsewhere - capsizePicks.length);
  const atMax = capsizePicks.length + usedElsewhere >= maxBudget;

  const toggleCapsize = (yoleId) => {
    if (capsizePicks.includes(yoleId)) {
      onCapsizeChange(capsizePicks.filter((id) => id !== yoleId));
    } else {
      if (atMax) {
        if (budget?.grandfathered) {
          toast.error(
            `Tu ne peux plus ajouter de dessalage — tu as déjà ${budget.used} paris. Tu peux en retirer pour en remettre.`,
          );
        } else {
          toast.error(
            `Budget saison épuisé — ${maxBudget}/${maxBudget} dessalages déjà pronostiqués sur cette saison.`,
          );
        }
        return;
      }
      onCapsizeChange([...capsizePicks, yoleId]);
    }
  };

  const pointsFor = (i) => yoles.length - i;
  const totalPossible = chosen.reduce((sum, _, i) => sum + pointsFor(i), 0);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-white border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
            Ton classement ({chosen.length}/{yoles.length})
          </div>
          <div className="text-[11px] font-mono text-slate-500">
            Max {totalPossible} pts
          </div>
        </div>
        {chosen.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-3">Ajoute tes yoles depuis la liste ci-dessous, dans l'ordre où tu penses qu'elles vont finir.</div>
        ) : (
          <div className="space-y-2">
            {chosen.map((yid, i) => {
              const y = byId[yid];
              if (!y) return null;
              const pts = pointsFor(i);
              return (
                <div key={yid} data-testid={`ranking-slot-${i}`} className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="w-8 h-8 rounded-full bg-[color:var(--deep)] text-white font-mono font-black text-sm flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </div>
                  {y.photo_url ? (
                    <img src={photoUrl(y.photo_url)} alt={y.name} className="w-9 h-9 rounded-lg object-cover ring-2 ring-white shadow" style={{ backgroundColor: y.color }} />
                  ) : (
                    <div className="w-9 h-9 rounded-lg ring-2 ring-white shadow flex-shrink-0" style={{ backgroundColor: y.color }} />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm truncate">{y.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{y.skipper || "—"}</div>
                  </div>
                  <div className="font-mono font-bold text-[color:var(--palm)] text-sm">+{pts}</div>
                  <div className="flex flex-col gap-0.5">
                    <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} data-testid={`ranking-up-${i}`} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30">
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" onClick={() => move(i, i + 1)} disabled={i === chosen.length - 1} data-testid={`ranking-down-${i}`} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30">
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <button type="button" onClick={() => remove(yid)} data-testid={`ranking-remove-${i}`} className="text-[10px] font-bold uppercase text-red-500 hover:underline">
                    Retirer
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {chosen.length < minSize && (
          <div className="mt-3 text-xs text-[color:var(--coral)] font-semibold">
            Ajoute au moins {minSize} yoles pour valider ton pronostic (min. top {minSize}).
          </div>
        )}
      </div>

      {notChosen.length > 0 && (
        <div className="rounded-2xl bg-white border border-slate-200 p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
            Yoles à placer
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {notChosen.map((y) => (
              <button
                key={y.id}
                type="button"
                onClick={() => add(y.id)}
                data-testid={`ranking-add-${y.id}`}
                className="flex items-center gap-2 p-2 rounded-xl border border-slate-200 hover:border-[color:var(--coral)] hover:scale-[1.02] transition text-left bg-white"
              >
                {y.photo_url ? (
                  <img src={photoUrl(y.photo_url)} alt={y.name} className="w-8 h-8 rounded-lg object-cover ring-2 ring-white shadow" style={{ backgroundColor: y.color }} />
                ) : (
                  <div className="w-8 h-8 rounded-lg ring-2 ring-white shadow flex-shrink-0" style={{ backgroundColor: y.color }} />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-xs truncate">{y.name}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-slate-200 p-4">
        <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Waves className="h-4 w-4 text-[color:var(--coral)] flex-shrink-0" />
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
              Yoles qui vont dessaler <span className="text-[color:var(--palm)] normal-case">(+{CAPSIZE_POINTS} pts par bonne prédiction)</span>
            </div>
          </div>
          <div
            data-testid="capsize-counter"
            className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded-full ${
              remainingSeason === 0
                ? "bg-[color:var(--coral)] text-white"
                : "bg-slate-100 text-slate-500"
            }`}
            title="Total sur la saison"
          >
            {usedElsewhere + capsizePicks.length}/{maxBudget}
          </div>
        </div>
        {budget?.grandfathered ? (
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-2 mb-2">
            <b>Note :</b> tu avais déjà pronostiqué <b>{budget.used}</b> dessalages avant la nouvelle règle
            (max <b>{budget.official_max ?? SEASON_CAPSIZE_BUDGET}</b> par saison). Tu gardes tes paris et
            peux les modifier — tu ne peux simplement pas en ajouter d'autres.
          </div>
        ) : (
          <div className="text-[11px] text-slate-500 mb-2">
            Budget saison : <b>{maxBudget} dessalages max</b> — il te reste{" "}
            <b className={remainingSeason === 0 ? "text-[color:var(--coral)]" : "text-[color:var(--palm)]"}>
              {remainingSeason}
            </b>{" "}
            pronostic(s) à répartir sur les étapes.
          </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {yoles.map((y) => {
            const active = capsizePicks.includes(y.id);
            const disabled = !active && atMax;
            return (
              <button
                key={y.id}
                type="button"
                onClick={() => toggleCapsize(y.id)}
                data-testid={`capsize-pick-${y.id}`}
                disabled={disabled}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-bold border transition ${
                  active
                    ? "border-[color:var(--coral)] bg-red-50 text-[color:var(--coral)]"
                    : disabled
                    ? "border-slate-200 bg-slate-50 text-slate-300 cursor-not-allowed"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                }`}
              >
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: y.color }} />
                {y.name}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
